import { IGraph, INode } from '@yfiles/yfiles';
import { KeyUtils } from './keyUtils';
import { PercentageMetrics } from './types';
import {
  APP_GROUP_NODE_HEIGHT,
  APP_GROUP_NODE_WIDTH,
  CAPABILITY_NODE_HEIGHT,
  CAPABILITY_NODE_WIDTH,
  LEAF_NODE_HEIGHT,
  LEAF_NODE_WIDTH,
  NODE_HEIGHT_MIN,
  NODE_HEIGHT_PERCENTAGE_CAP,
  NODE_HEIGHT_RANGE,
  TEAM_NODE_HEIGHT,
  TEAM_NODE_WIDTH,
  THRESHOLD_GOOD,
  THRESHOLD_WARNING
} from './constants';

export function calculateDynamicHeight(percentage: number): number {
  const capped = Math.min(percentage, NODE_HEIGHT_PERCENTAGE_CAP);
  return NODE_HEIGHT_MIN + (capped * NODE_HEIGHT_RANGE) / 100;
}

import {
  alarmLabelLayoutParameter,
  appNodeTitleLabelLayoutParameter,
  appNodeTitleLabelStyle,
  getLeafNodeStyle,
  leafNameLabelLayoutParameter,
  leafNameLabelStyle,
  percentageLabelLayoutParameter,
  percentageLabelStyle
} from './styles';
import { getTeamLabel } from '../../../services/TeamLabelService';
import {
  alarmIconLabelStyle,
  agentGroupNodeIconLabelStyle,
  applicationGroupNodeIconLabelStyle,
  capabilityGroupNodeIconLabelStyle,
  getApplicationGroupNodeStyle,
  getCapabilityGroupNodeStyle,
  getTeamGroupNodeStyle,
  groupNodeIconLabelLayoutParameter,
  groupNodePercentageLabelLayoutParameter,
  groupNodePercentageLabelStyle,
  groupNodeTitleLabelLayoutParameter,
  groupNodeTitleLabelStyle,
  HeatMapColor,
  heatMapLabelFill,
  teamGroupNodeIconLabelStyle
} from './groupNodeStyles.ts';

interface NodeCreationContext {
  graph: IGraph;
  nodeMap: Map<string, INode>;
  nodeId: { value: number };
  capabilityMap: Map<string, any>;
  applicationGroupMap: Map<string, any>;
}

/**
 * Determines the color for a percentage value based on thresholds
 * @param percentage - The percentage value as a string (e.g. '65%')
 * @returns The appropriate heat map color
 */
export function getStatusFromPercentage(percentage: number): HeatMapColor {
  if (percentage >= THRESHOLD_GOOD) {
    return 'GOOD';
  } else if (percentage >= THRESHOLD_WARNING && percentage < THRESHOLD_GOOD) {
    return 'WARNING';
  } else {
    return 'BAD';
  }
}

/**
 * Adds the percentage labels for group nodes (capability, team, application group).
 * If status is BAD, adds an underneath background label to give pulsing shadow effect.
 */
function addGroupPercentageLabels(
  graph: IGraph,
  owner: INode,
  percentage?: PercentageMetrics,
  performanceMetric: string = 'Health'
): void {
  if (!percentage) {
    return;
  }
  const displayPercentage = percentage.getPercentageByMetric(performanceMetric);
  const status = getStatusFromPercentage(displayPercentage);
  const style = groupNodePercentageLabelStyle.clone();

  // Check if this category has metrics defined and has timeseries data
  const hasCategoryData = percentage.hasCategoryMetrics(performanceMetric);
  const hasData = percentage.hasTimeseriesData && hasCategoryData;

  // If no data for this category, keep the default grey background; otherwise apply heat map color
  if (hasData) {
    style.backgroundFill = heatMapLabelFill[status];
  }

  if (hasData && status === 'BAD') {
    const underneathStyle = style.clone();
    underneathStyle.cssClass = 'group-node-style-percentage-underneath';
    graph.addLabel({
      owner,
      text: displayPercentage + '%',
      style: underneathStyle,
      layoutParameter: groupNodePercentageLabelLayoutParameter
    });
  }

  graph.addLabel({
    owner,
    text: displayPercentage + '%',
    style,
    layoutParameter: groupNodePercentageLabelLayoutParameter
  });
}

/**
 * Creates a capability group node in the graph.
 * @param context - The node creation context
 * @param capabilityName - Name of the capability
 * @param stageName - Name of the stage
 * @param heatMapColor
 * @param percentage
 * @param percentageColor
 * @returns The created node or null if capability not found
 */
export function createCapaGroupNode(
  context: NodeCreationContext,
  capabilityName: string,
  stageName: string,
  percentage: PercentageMetrics = new PercentageMetrics(95, 95, 95, 95, 95, 95)
): INode | null {
  const { graph, nodeMap, nodeId, capabilityMap } = context;
  const capability = capabilityMap.get(KeyUtils.forCapabilityGroup(capabilityName, stageName));

  if (capability) {
    const nodeKey = KeyUtils.forCapabilityGroup(capabilityName, stageName);
    if (nodeMap.has(nodeKey)) {
      return nodeMap.get(nodeKey)!;
    }

    // Get color based on percentage (using Health as default)
    const status = getStatusFromPercentage(percentage.HealthPercentage);

    const capabilityNode = graph.createGroupNode({
      layout: [0, 0, CAPABILITY_NODE_WIDTH, CAPABILITY_NODE_HEIGHT],
      tag: {
        ...capability,
        nodeId: nodeId.value++,
        type: 'CAPABILITY',
        stage: stageName,
        percentage
      },
      style: getCapabilityGroupNodeStyle(status)
    });

    graph.addLabel({
      owner: capabilityNode,
      text: 'Capability Instance',
      style: capabilityGroupNodeIconLabelStyle,
      layoutParameter: groupNodeIconLabelLayoutParameter
    });

    graph.addLabel({
      owner: capabilityNode,
      text: capability.Name,
      style: groupNodeTitleLabelStyle,
      layoutParameter: groupNodeTitleLabelLayoutParameter,
      tag: { tooltip: capability.Name }
    });

    addGroupPercentageLabels(graph, capabilityNode, percentage);

    nodeMap.set(nodeKey, capabilityNode);
    return capabilityNode;
  }
  return null;
}

/**
 * Creates a team node in the graph under the specified parent.
 * @param context - The node creation context
 * @param teamName - Name of the team
 * @param parent - Parent node (usually a capability node)
 * @param stageName - Name of the stage
 * @param heatMapColor
 * @param percentage
 * @param percentageColor
 * @returns The created team node
 */
export function createTeamNode(
  context: NodeCreationContext,
  teamName: string,
  parent: INode,
  stageName: string | undefined,
  percentage: PercentageMetrics = new PercentageMetrics(75, 75, 75, 75, 75, 75)
): INode {
  const { graph, nodeMap, nodeId } = context;
  const nodeKey = KeyUtils.forTeamNode(teamName, parent.tag.Name, stageName || '');

  if (nodeMap.has(nodeKey)) {
    return nodeMap.get(nodeKey)!;
  }

  // Get color based on percentage (using Health as default)
  var status = getStatusFromPercentage(percentage.HealthPercentage);

  const teamNode = graph.createGroupNode({
    parent,
    layout: [0, 0, TEAM_NODE_WIDTH, TEAM_NODE_HEIGHT],
    tag: {
      Name: teamName,
      nodeId: nodeId.value++,
      type: 'TEAM',
      team: teamName,
      stage: stageName,
      percentage
    },
    style: getTeamGroupNodeStyle(status)
  });

  graph.addLabel({
    owner: teamNode,
    text: getTeamLabel(),
    style: getTeamLabel() === 'Agent' ? agentGroupNodeIconLabelStyle : teamGroupNodeIconLabelStyle,
    layoutParameter: groupNodeIconLabelLayoutParameter
  });

  graph.addLabel({
    owner: teamNode,
    text: teamName,
    style: groupNodeTitleLabelStyle,
    layoutParameter: groupNodeTitleLabelLayoutParameter,
    tag: { tooltip: teamName }
  });

  addGroupPercentageLabels(graph, teamNode, percentage);

  nodeMap.set(nodeKey, teamNode);
  return teamNode;
}

/**
 * Creates an application group node in the graph.
 * @param context - The node creation context
 * @param applicationGroupName - Name of the application group
 * @param parent - Parent node (usually a team node)
 * @param capabilityName - Name of the capability
 * @param stageName - Name of the stage
 * @param heatMapColor
 * @param percentage
 * @param percentageColor
 * @returns The created application group node
 */
export function createAppGroupNode(
  context: NodeCreationContext,
  applicationGroupName: string,
  parent: INode,
  capabilityName: string,
  stageName?: string,
  percentage: PercentageMetrics = new PercentageMetrics(0, 0, 0, 0, 0, 0)
): INode {
  const { graph, nodeMap, nodeId, applicationGroupMap } = context;
  const teamName = parent.tag.team;
  const nodeKey = KeyUtils.forAppGroup(
    applicationGroupName,
    teamName,
    capabilityName,
    stageName || ''
  );

  if (nodeMap.has(nodeKey)) {
    return nodeMap.get(nodeKey)!;
  }

  const appGroup = applicationGroupMap.get(applicationGroupName);

  // Get color based on percentage (using Health as default)
  const status = getStatusFromPercentage(percentage.HealthPercentage);

  const appGroupNode = graph.createGroupNode({
    parent,
    layout: [0, 0, APP_GROUP_NODE_WIDTH, APP_GROUP_NODE_HEIGHT],
    tag: {
      ...appGroup,
      nodeId: nodeId.value++,
      type: 'APPLICATION_GROUP',
      team: teamName,
      stage: stageName,
      percentage
    },
    style: getApplicationGroupNodeStyle(status)
  });

  graph.addLabel({
    owner: appGroupNode,
    text: 'Application',
    style: applicationGroupNodeIconLabelStyle,
    layoutParameter: groupNodeIconLabelLayoutParameter
  });

  graph.addLabel({
    owner: appGroupNode,
    text: applicationGroupName,
    style: groupNodeTitleLabelStyle,
    layoutParameter: groupNodeTitleLabelLayoutParameter,
    tag: { tooltip: applicationGroupName }
  });

  addGroupPercentageLabels(graph, appGroupNode, percentage);

  nodeMap.set(nodeKey, appGroupNode);
  return appGroupNode;
}

/**
 * Creates an application node in the graph.
 * @param context - The node creation context
 * @param appName - Name of the application
 * @param parent - Parent node (usually an application group node)
 * @param capabilityName - Name of the capability
 * @param stageName - Name of the stage
 * @param leafName - Optional leaf name for more specific identification
 * @param type
 * @param percentage - A percentage value
 * @param percentageColor - Optional heat map color for the percentage label
 * @returns The created application node
 */
export function createLeafNode(
  context: NodeCreationContext,
  appName: string,
  parent: INode,
  capabilityName: string,
  stageName: string,
  leafName = 'leaf name',
  type: string,
  percentage: PercentageMetrics
): INode {
  const { graph, nodeMap, nodeId } = context;
  const teamName = parent.tag.team || parent.tag.Name;
  const parentName = parent.tag.Name;

  const nodeKey = leafName
    ? KeyUtils.forLeafNode(leafName, appName, teamName, capabilityName, stageName)
    : KeyUtils.forAppNode(appName, parentName, teamName, capabilityName, stageName);

  if (nodeMap.has(nodeKey)) {
    return nodeMap.get(nodeKey)!;
  }

  const displayName = leafName || appName;

  // Calculate 3D height based on HealthPercentage (average of all metrics)
  const calculatedHeight = calculateDynamicHeight(percentage.HealthPercentage);

  const appNode = graph.createNode({
    parent,
    layout: [0, 0, LEAF_NODE_WIDTH, LEAF_NODE_HEIGHT],
    tag: {
      name: displayName,
      nodeId: nodeId.value++,
      type: 'LEAF',
      team: parent.tag.team,
      stage: stageName,
      parentName: parent.tag.Name,
      leafName: leafName,
      capability: capabilityName,
      percentage,
      height: calculatedHeight,
      subtype: type
    },
    style: getLeafNodeStyle(percentage.HealthPercentage)
  });

  graph.addLabel({
    owner: appNode,
    text: displayName,
    style: appNodeTitleLabelStyle,
    layoutParameter: appNodeTitleLabelLayoutParameter,
    tag: { tooltip: displayName }
  });

  graph.addLabel({
    owner: appNode,
    text: type,
    style: leafNameLabelStyle,
    layoutParameter: leafNameLabelLayoutParameter,
    tag: { tooltip: leafName }
  });

  const style = percentageLabelStyle.clone();
  const status = getStatusFromPercentage(percentage.HealthPercentage);
  style.textFill = heatMapLabelFill[status];

  graph.addLabel({
    owner: appNode,
    text: percentage.HealthPercentage + '%',
    style,
    layoutParameter: percentageLabelLayoutParameter
  });

  if (status === 'BAD') {
    graph.addLabel({
      owner: appNode,
      text: '',
      style: alarmIconLabelStyle,
      layoutParameter: alarmLabelLayoutParameter,
      tag: { type: 'ALARM_ICON' }
    });
    let haloStyle = alarmIconLabelStyle.clone();
    haloStyle.cssClass = 'alarm-icon-halo';

    graph.addLabel({
      owner: appNode,
      text: '',
      style: haloStyle,
      layoutParameter: alarmLabelLayoutParameter,
      tag: { type: 'ALARM_HALO' }
    });
  }

  nodeMap.set(nodeKey, appNode);
  return appNode;
}
